import React from 'react';

import { Button, Group, Modal, Stack, Text, Title } from '@mantine/core';
import { useForm } from '@mantine/form';
import { AxiosError, AxiosResponse } from 'axios';
import PaymentMethodSelect from 'components/form-fields/auto-complete-fields/PaymentMethodSelect';
import { UseMutationResult, useQueryClient } from 'react-query';
import {
  AthletePackageRetrieve,
  AthletePackageUpdate,
  AthletePackageUpdateResponse,
  ErrorMessage,
} from 'types';
import { NonStripePaymentMethod } from 'types/enums';
import notificationHandler from 'utils/notifications';

import { paymentMethodSuccessMessage } from './chainPropagationMessages';

interface IChangePaymentMethodModalProps {
  opened: boolean;
  athletePackage: AthletePackageRetrieve;
  closeHandler: () => void;
  updateAthletePackageMutation: UseMutationResult<
    AxiosResponse<AthletePackageUpdateResponse>,
    AxiosError<ErrorMessage>,
    AthletePackageUpdate
  >;
}

interface FormValues {
  athletePackageId: string;
  paymentMethod: string | null;
  nonStripePaymentMethod: 'Cash or Check' | 'Gift Card' | 'E Transfer' | 'POS Terminal' | null;
}

const ChangePaymentMethodModal = ({
  opened,
  athletePackage,
  closeHandler,
  updateAthletePackageMutation,
}: IChangePaymentMethodModalProps): JSX.Element => {
  const { successNotification, errorNotification } = notificationHandler();
  const queryClient = useQueryClient();

  const form = useForm<FormValues>({
    initialValues: {
      athletePackageId: athletePackage.athletePackageId,
      paymentMethod: athletePackage.paymentMethod,
      nonStripePaymentMethod: athletePackage.nonStripePaymentMethod as NonStripePaymentMethod | null,
    },
  });

  const handlePaymentChange = (value: string): void => {
    if (
      Object.values(NonStripePaymentMethod).includes(value as NonStripePaymentMethod) &&
      athletePackage.autoPay
    ) {
      errorNotification(
        'Invalid Payment Method',
        'Auto Pay is enabled for this package. Please disable Auto Pay before selecting a non-stripe payment method.',
      );
      return;
    }
    form.setFieldValue('paymentMethod', value);
    if (Object.values(NonStripePaymentMethod).includes(value as NonStripePaymentMethod)) {
      form.setFieldValue('nonStripePaymentMethod', value as NonStripePaymentMethod);
    } else {
      form.setFieldValue('nonStripePaymentMethod', null);
    }
  };

  const handleSubmit = (formValues: FormValues): void => {
    const payload: AthletePackageUpdate = {
      action: 'payment_method',
      athletePackageId: athletePackage.athletePackageId,
      autoRenew: athletePackage.autoRenew,
      autoPay: athletePackage.autoPay,
      paymentMethod: formValues.nonStripePaymentMethod ? null : formValues.paymentMethod,
      nonStripePaymentMethod: formValues.nonStripePaymentMethod,
      athleteStatusOnStart: athletePackage.athleteStatusOnStart,
      athleteStatusOnEnd: athletePackage.athleteStatusOnEnd,
    };
    updateAthletePackageMutation.mutate(payload, {
      onError: error => {
        errorNotification('Athlete package update failed', error.response?.data.message);
      },
      onSuccess: response => {
        queryClient.invalidateQueries(['invoices']);
        successNotification(
          'Athlete package update success',
          paymentMethodSuccessMessage(response.data.chainPropagation),
        );
        closeHandler();
      },
    });
  };

  return (
    <Modal
      opened={opened}
      onClose={closeHandler}
      size='auto'
      centered
      title={<Title order={4}>Payment Method</Title>}
    >
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack p='md' justify='center'>
          <Text color='dimmed' size='sm'>
            This change will be applied to this package, the other active, upcoming and on-hold packages
            linked to it, and their unpaid invoices.
          </Text>
          <PaymentMethodSelect
            placeholder='Select one'
            athleteId={athletePackage.athlete}
            allowCash={true}
            label='Payment Method'
            onChange={(value: string) => handlePaymentChange(value)}
            value={form.values.paymentMethod}
            dropdownPosition='bottom'
          />
          <Group position='right' pt='md'>
            <Button
              variant='light'
              onClick={closeHandler}
              size='xs'
              radius='md'
              disabled={updateAthletePackageMutation.isLoading}
            >
              Cancel
            </Button>
            <Button size='xs' radius='md' type='submit' loading={updateAthletePackageMutation.isLoading}>
              Save
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
};

export default ChangePaymentMethodModal;
